import { toZonedTime } from 'date-fns-tz'
import moment from 'moment-timezone'

import { TIMEZONES } from '../../../helpers/constants'

const getDuration = (timeStart, timeEnd) => {
  const momentTimeEnd = moment(timeEnd)
  const momentTimeStart = moment(timeStart).set('date', momentTimeEnd.date())
  return Math.abs(momentTimeEnd.diff(momentTimeStart, 'minute'))
}

const fetchFileFromRemote = (url) => {
  return fetch(url)
    .then((response) => response.blob())
    .then((blob) => blob)
}

const mergeDateWithTimeStart = (date, timeStart, buildingTimezone) => {
  const userSelectedDate = moment(date).format('YYYY-MM-DD')

  const selectedHours = moment(timeStart).hours()
  const selectedMinutes = moment(timeStart).minutes()

  const result = moment
    .tz(userSelectedDate, buildingTimezone)
    .hours(selectedHours)
    .minutes(selectedMinutes)
    .seconds(0)
    .milliseconds(0)

  return result.valueOf()
}

export const getInitialTimeStart = (timezone) => {
  const dateNow = toZonedTime(new Date(), timezone)
  const now = moment(dateNow).add(1, 'hour')
  const reminder = 15 - (now.minutes() % 15)
  return moment(now).add(reminder, 'minutes')
}

export const getInitialTimeEnd = (timezone) => {
  const now = getInitialTimeStart(timezone)
  return now.add(15, 'minutes')
}

export const handleTimeChange = (event, handleFieldChange, start, end) => {
  const {
    target: { name, value },
  } = event
  const isStartTime = name === 'timeStart'
  const isEndTime = name === 'timeEnd'
  const selectedTime = moment(value)
  if (isStartTime) {
    if (selectedTime.isSameOrAfter(moment(end), 'minutes')) {
      handleFieldChange({ target: { name: 'timeEnd', value: selectedTime.add(15, 'minutes') } })
    }
    return handleFieldChange(event)
  }
  if (isEndTime) {
    if (selectedTime.isSameOrBefore(moment(start), 'minutes')) {
      handleFieldChange({
        target: { name: 'timeStart', value: selectedTime.subtract(15, 'minutes') },
      })
    }
    return handleFieldChange(event)
  }
}

export const getCheckboxHandler = (handleFieldChange) => {
  return (event) => {
    const {
      target: { checked, name },
    } = event
    handleFieldChange({ target: { name, value: checked } })
  }
}

const getReminderTime = (timeString) => {
  const [amount, timeUnit] = timeString.split(' ')
  if (timeUnit === 'hours') {
    return amount
  }
  if (timeUnit === 'hour') {
    return 1
  }
  return amount * 24
}

export const getIdFromObjectOrValue = (objectOrValue) => {
  if (typeof objectOrValue === 'object') {
    return objectOrValue.id
  }
  return objectOrValue
}

const getReminderFields = (formState) => {
  const data = {}
  if (!formState.setupAutomatedReminders) {
    return {}
  }
  data.remindAll = formState.nonRsvpd
  data.remindRsvp = formState.rsvpd
  if (formState.nonRsvpd) {
    data.remindAllBeforeHours = getReminderTime(formState.nonRsvpdTime)
  }
  if (formState.rsvpd) {
    data.remindRsvpBeforeHours = getReminderTime(formState.rsvpdTime)
  }
  return data
}

const getCommonRequestFields = async (formState, eventType) => {
  const reminderData = getReminderFields(formState)

  // Calculate the date value with mergeDateWithTimeStart
  const eventDate = mergeDateWithTimeStart(
    formState.date,
    formState.timeStart,
    formState.buildingData.timezone,
  )

  return {
    ...reminderData,
    type: eventType,
    name: formState.eventName,
    description: formState.description,
    place: formState.location,
    date: eventDate,
    duration: getDuration(formState.timeStart, formState.timeEnd),
    schedulerType:
      formState.schedulerType && formState.schedulerType.value !== 'none'
        ? formState.schedulerType.value
        : null,
  }
}

const getFileFields = async (formState) => {
  const image = formState.image.url
    ? await fetchFileFromRemote(formState.image.url)
    : formState.image
  return {
    ...(typeof formState.image !== 'string' && { image }),
    ...(formState.pdf && typeof formState.pdf !== 'string' && { file: formState.pdf }),
  }
}

const getBuildingEventRequestFields = (
  formState,
  eventTypeOptions,
  isPropertyManagerOrFrontDesk,
) => {
  const eventLabel = isPropertyManagerOrFrontDesk
    ? []
    : eventTypeOptions.find((type) => type.value === getIdFromObjectOrValue(formState.eventType))
        .label
  return {
    buildingId: getIdFromObjectOrValue(formState.building),
    event: true,
    ...(!isPropertyManagerOrFrontDesk && { trainerId: getIdFromObjectOrValue(formState.trainer) }),
    ...(!isPropertyManagerOrFrontDesk && { type: eventLabel }),
    capacity: formState.eventCapacity,
    cost: formState.eventPrice ? formState.eventPrice : 0,
    guestsAmount: formState.guestsAmount ? formState.guestsAmount : 0,
    socialLinkService:
      formState.socialLinkService &&
      formState.positiveReviewFollowup &&
      formState.askForPostEventReview
        ? formState.socialLinkService
        : null,
    signUpLink: !formState.collectRSVP ? '' : formState.signUpLink || '',
    collectGuestsInfo: formState.collectGuestInformation,
    householdMembersAllowed: formState.householdMembersAllowed,
    limitGuestsPerResident: formState.limitGuestsPerResident,
  }
}

const getClassEventRequestFields = (formState, eventTypeOptions) => {
  const eventLabel = eventTypeOptions.find(
    (type) => type.value === getIdFromObjectOrValue(formState.eventType),
  ).label
  return {
    buildingId: getIdFromObjectOrValue(formState.building),
    event: false,
    trainerId: getIdFromObjectOrValue(formState.trainer),
    capacity: formState.eventCapacity,
    type: eventLabel,
    createEventSurvey: formState.askForPostEventReview,
    socialLinkService:
      formState.socialLinkService &&
      formState.positiveReviewFollowup &&
      formState.askForPostEventReview
        ? formState.socialLinkService
        : null,
  }
}

const getReminderRequestFields = (formState) => {
  return {
    buildingId: getIdFromObjectOrValue(formState.building),
    event: false,
    capacity: 0,
  }
}

export const getAppointmentEventRequestFields = (formState, eventTypeOptions) => {
  const eventLabel = eventTypeOptions.find(
    (type) => type.value === getIdFromObjectOrValue(formState.eventType),
  ).label
  return {
    buildingId: formState.buildingData.id,
    type: eventLabel,
    event: false,
    trainerId: getIdFromObjectOrValue(formState.trainer),
    mainClient: getIdFromObjectOrValue(formState.client),
    capacity: 0,
    createEventSurvey: formState.askForPostEventReview,
    socialLinkService:
      formState.socialLinkService &&
      formState.positiveReviewFollowup &&
      formState.askForPostEventReview
        ? formState.socialLinkService
        : null,
  }
}

export const transformFormStateToRequestData = async (
  formState,
  eventType,
  eventTypeOptions,
  isPropertyManagerOrFrontDesk,
  socialLinks,
) => {
  let eventFields = {}
  const commonRequestFields = await getCommonRequestFields(formState, eventType)
  const files = await getFileFields(formState)
  switch (eventType) {
    case 'Building Event':
      eventFields = getBuildingEventRequestFields(
        formState,
        eventTypeOptions,
        isPropertyManagerOrFrontDesk,
      )
      break
    case 'Appointment':
      eventFields = getAppointmentEventRequestFields(formState, eventTypeOptions, socialLinks)
      break
    case 'Class':
      eventFields = getClassEventRequestFields(formState, eventTypeOptions, socialLinks)
      break
    case 'Reminder':
      eventFields = getReminderRequestFields(formState)
      break
  }

  return {
    ...commonRequestFields,
    ...eventFields,
    ...files,
  }
}

export const getRemindTimeFromEventData = (time) => {
  if (time > 24) {
    return `${time / 24} days`
  } else if (time === 1) {
    return `${time} hour`
  } else {
    return `${time} hours`
  }
}

export const transformEventDataToFormState = (eventData) => {
  const rsvpdTime = eventData.remindRsvpBeforeHours
    ? getRemindTimeFromEventData(eventData.remindRsvpBeforeHours)
    : null
  const nonRsvpdTime = eventData.remindAllBeforeHours
    ? getRemindTimeFromEventData(eventData.remindAllBeforeHours)
    : null
  const client =
    eventData.mainClient && eventData.mainClient.user
      ? {
          ...eventData.mainClient,
          label: `${eventData.mainClient.user.firstName} ${eventData.mainClient.user.lastName}`,
          value: eventData.mainClient.id,
        }
      : null
  return {
    building: eventData.buildingId.id,
    isHoa: eventData.buildingId.isHoa,
    eventType: eventData.type.id,
    trainer: eventData.trainerId.id,
    eventName: eventData.name,
    description: eventData.description,
    location: eventData.place,
    date: moment(eventData.date),
    timeStart: moment(eventData.start),
    timeEnd: moment(eventData.end),
    eventCapacity: eventData.capacity,
    eventPrice: eventData.cost,
    askForPostEventReview: eventData.createEventSurvey || false,
    positiveReviewFollowup: !!eventData.socialLinkService || false,
    setupAutomatedReminders: eventData.remindAll || eventData.remindRsvp,
    rsvpd: eventData.remindRsvp,
    nonRsvpd: eventData.remindAll,
    rsvpdTime,
    nonRsvpdTime,
    guestsAmount: eventData.guestsAmount,
    image: eventData.image,
    pdf: eventData.file,
    imagesDatabaseOpen: false,
    collectGuestInformation: eventData.collectGuestsInfo,
    socialLinkService: eventData.socialLinkService,
    collectRSVP: !!eventData.signUpLink,
    signUpLink: eventData.signUpLink,
    client,
    householdMembersAllowed: eventData.householdMembersAllowed,
    limitGuestsPerResident: !!eventData.guestsAmount,
  }
}

export const getBuildingOptionsPmOrFd = (isPm, isFd, userData) => {
  if (isPm) {
    return userData.buildingIds.map((building) => ({
      ...building,
      value: building.id,
      label: building.name,
    }))
  }
  if (isFd) {
    return [
      {
        value: userData.buildingId.id,
        label: userData.buildingId.name,
        id: userData.buildingId.id,
        timezone: userData.buildingId.timezone,
      },
    ]
  }
}

export const getOption = (valueOrObject, options) => {
  if (typeof valueOrObject === 'object') return valueOrObject
  return options.find((opt) => opt.value === valueOrObject)
}

export const getTimezone = (formState, buildingOptions) => {
  return formState.building
    ? (
        buildingOptions.find(
          (building) => building.id === getIdFromObjectOrValue(formState.building),
        ) || {}
      ).timezone
    : TIMEZONES.CT
}

export const getFrequencyOptions = (date, t) => {
  const dateMoment = moment(date)
  const dayOfWeek = dateMoment.format('dddd') // e.g., "Monday"
  const dayOfWeekWithDate = dateMoment.format('Do') // e.g., "15th"

  const weekOccurrence = Math.ceil(dateMoment.date() / 7) // Determine which week (1st, 2nd, etc.)
  const ordinalKeys = ['first', 'second', 'third', 'fourth']
  const ordinalKey = ordinalKeys[weekOccurrence - 1] || 'fourth' // Fallback to fourth if out of range
  const ordinal = t(`create-event:helpers.ordinals.${ordinalKey}`)
  return [
    { value: 'none', label: t('create-event:helpers.frequencyOptions.doesNotRepeat') },
    { value: 'daily', label: t('create-event:helpers.frequencyOptions.daily') },
    { value: 'weekdays', label: t('create-event:helpers.frequencyOptions.weekdaysOnly') },
    { value: 'weekly', label: t('create-event:helpers.frequencyOptions.weeklyOn', { dayOfWeek }) },
    {
      value: 'monthly',
      label: t('create-event:helpers.frequencyOptions.monthlyOn', { dayOfWeekWithDate }),
    },
    {
      value: 'monthly-on-day',
      label: t('create-event:helpers.frequencyOptions.monthlyOnThe', { ordinal, dayOfWeek }),
    },
  ]
}

export function getBaseState(state, timezone = TIMEZONES.CT) {
  const timeStart = getInitialTimeStart(timezone)
  const timeEnd = getInitialTimeEnd(timezone)
  return {
    timeStart,
    timeEnd,
    date: moment(toZonedTime(new Date(), timezone)),
    ...state,
  }
}
